/**
 * Host (Zoom) selection concern for the Details step.
 *
 * Extracted verbatim from `DetailsStep` (pure file separation — no
 * behaviour change). Owns: current-user zoom status, the host-option
 * lists (`selectedHostParticipantOptions`, `editSavedHost`), the
 * SCH-37 lock resolution + enforcer, the CREATE-only auto-pick effect
 * (carrying the CORE-4039 edit-preserve rule), and the Zoom-location
 * auto-fallback. Memo/effect bodies and their dependency arrays are
 * unchanged; the only additions are the stable `formRef` ref in the
 * three effect dep arrays (it was a component-local `useRef`
 * pre-extraction, so the original arrays omitted it — listing it now
 * satisfies exhaustive-deps across the hook boundary; its identity
 * never changes so behaviour is identical).
 *
 * Called at the same hook-order position in `DetailsStep`, so React's
 * hook call order is preserved.
 */
import {
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { useSelector } from 'react-redux';
import type { FormBuilderHandle } from '@kula-ai/components';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useLocationFieldConfig } from '@/formConfigs';
import {
  isHostOptionDisabled,
  type HostZoomStatus,
} from '../fields/HostRenders';
import {
  pickAutoHostId,
  isExplicitlyInvalidHost,
} from '../helpers/pickAutoHostId';
import { resolveLockedHostId } from '../helpers/resolveLockedHostId';
import { getCachedInterviewerOption } from '../../../state/interviewerOptionCache';
import type {
  ApiReference,
  ScheduleInterviewPayload,
} from '../../../config/types';
import type { ScheduleSharedState } from '../../../state/useScheduleSharedState';
import type { CurrentUserZoomStatus } from '../../../initialValues/useWizardInitialValues';

interface HostOption {
  value: string;
  label: string;
  image?: string;
  zoomStatus?: HostZoomStatus;
  /** Google Meet host eligibility (BE `can_host_gmeet_meeting`), threaded
   *  alongside zoomStatus so the Meet picker resolves it inline. */
  meetEligible?: boolean;
}

interface UseScheduleHostSelectionArgs {
  interviewPlan?: Record<string, unknown>;
  currentUserZoomStatus?: CurrentUserZoomStatus;
  payloadRef: MutableRefObject<ScheduleInterviewPayload>;
  sharedState: ScheduleSharedState;
  apiReference: ApiReference;
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  isSelfSchedule: boolean;
  formRef: RefObject<FormBuilderHandle | null>;
}

interface UseScheduleHostSelectionResult {
  /** The extra (non-interviewer) host candidate: the logged-in recruiter
   *  in create, the original interview creator in edit. NOT the editor. */
  currentUserHostInfo:
    | {
        id: number | string;
        label: string;
        image?: string;
        zoomStatus?: HostZoomStatus;
        meetEligible?: boolean;
      }
    | undefined;
  /** The REAL logged-in user id — for "you" semantics (not host choice). */
  loggedInUserId: number | string | undefined;
  selectedHostParticipantOptions: HostOption[];
  editSavedHost: HostOption | undefined;
}

export function useScheduleHostSelection({
  interviewPlan,
  currentUserZoomStatus,
  payloadRef,
  sharedState,
  apiReference,
  planZoomHostType: planZoomHostTypeProp,
  isSelfSchedule,
  formRef,
}: UseScheduleHostSelectionArgs): UseScheduleHostSelectionResult {
  // The Host Select renders only the picked interviewers + the current
  // user (legacy `SchZoomHost` constraint). zoom_status comes from the
  // create/edit API responses (`user_zoom_status` block + each
  // participant's zoom_status partial); for adhoc create with no plan
  // we leave it undefined and the chip skips the tag.
  const currUser = useSelector(selectCurrUser) as
    | {
        id?: number | string;
        first_name?: string;
        last_name?: string;
        avatar_url?: string;
        avatar?: string;
      }
    | null
    | undefined;

  // The current user's zoom status comes back in two different shapes
  // depending on the endpoint:
  //   - GET /ats_interviews/new (create-from-plan): top-level FLAT
  //     `can_host_zoom_meeting`, `zoom_rate_limit_reached`, `zoom_plan`,
  //     `zoom_plan_type` (rendered straight by `_zoom_status` partial
  //     at the root of the response).
  //   - GET /ats_interviews/:id (edit): NESTED under `user_zoom_status`
  //     for the original interview creator.
  // Memoised so the resulting reference is stable across renders that
  // don't actually change zoom-status values. Without this the
  // `currentUserHostInfo` useMemo depends on a fresh object every
  // render and the auto-pick effect (and any consumer that diffs on
  // its identity) re-fires unnecessarily.
  const userZoomStatus = useMemo<
    | (HostZoomStatus & {
        id?: number | string;
        can_host_gmeet_meeting?: boolean;
      })
    | null
  >(() => {
    const p = interviewPlan as Record<string, unknown> | undefined;
    const nested = (p?.user_zoom_status ?? null) as
      | (HostZoomStatus & {
          id?: number | string;
          can_host_gmeet_meeting?: boolean;
        })
      | null;
    if (nested) return nested;
    if (!p) {
      // Adhoc: `interviewPlan` is intentionally undefined so plan-
      // derived behavioral branches stay off. Zoom status arrives
      // separately as `currentUserZoomStatus` (pulled from the same
      // plan response in `useWizardInitialValues`). No `id` here —
      // the value represents the current user by construction, and
      // the `id`-match guard in `currentUserHostInfo` accepts
      // `id === undefined`.
      return currentUserZoomStatus ?? null;
    }
    if (
      'can_host_zoom_meeting' in p ||
      'zoom_plan' in p ||
      'zoom_rate_limit_reached' in p
    ) {
      return {
        can_host_zoom_meeting: p.can_host_zoom_meeting as boolean | undefined,
        zoom_rate_limit_reached: p.zoom_rate_limit_reached as
          | boolean
          | undefined,
        zoom_plan: p.zoom_plan as number | undefined,
        zoom_plan_type: p.zoom_plan_type as string | undefined,
        can_host_gmeet_meeting: p.can_host_gmeet_meeting as boolean | undefined,
      };
    }
    return null;
    // Intentional narrow deps: only the zoom fields we read here.
    // Listing the full `interviewPlan` would invalidate this memo
    // every time any unrelated key on the response changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interviewPlan?.user_zoom_status,
    interviewPlan?.can_host_zoom_meeting,
    interviewPlan?.zoom_plan,
    interviewPlan?.zoom_plan_type,
    interviewPlan?.zoom_rate_limit_reached,
    interviewPlan?.can_host_gmeet_meeting,
    currentUserZoomStatus,
  ]);

  const currentUserHostInfo = useMemo(() => {
    // EDIT: the extra (non-interviewer) host candidate is the user who
    // SCHEDULED the interview — `user_zoom_status` (= `ats_interview.user`,
    // "data of the user who created the interview" per the BE jbuilder),
    // NOT the logged-in editor. Clones legacy `SchZoomHost` prod behaviour
    // (`currentUserOption = isEdit ? user_zoom_status : currUser`): whoever
    // is currently editing must never surface as a selectable Zoom host
    // unless they're a picked interviewer (handled via `participantOptions`)
    // or the saved host (`editSavedHost`). Without this, editing an
    // interview someone else created leaked the editor into the Host
    // dropdown as a selectable option (SCH-66).
    if (apiReference === 'edit') {
      const creator = interviewPlan?.user_zoom_status as
        | (HostZoomStatus & {
            id?: number | string;
            name?: string;
            avatar?: string;
            avatar_url?: string;
            can_host_gmeet_meeting?: boolean;
          })
        | null
        | undefined;
      if (!creator?.id) return undefined;
      return {
        id: creator.id,
        label: creator.name ?? `User ${creator.id}`,
        image: creator.avatar_url ?? creator.avatar,
        zoomStatus: {
          can_host_zoom_meeting: creator.can_host_zoom_meeting,
          zoom_rate_limit_reached: creator.zoom_rate_limit_reached,
          zoom_plan: creator.zoom_plan,
          zoom_plan_type: creator.zoom_plan_type,
        },
        meetEligible: creator.can_host_gmeet_meeting,
      };
    }

    // CREATE / adhoc: the current logged-in user IS the scheduler, so they
    // remain the extra host candidate.
    if (!currUser?.id) return undefined;
    const fullName =
      `${currUser.first_name ?? ''} ${currUser.last_name ?? ''}`.trim() ||
      `User ${currUser.id}`;
    return {
      id: currUser.id,
      label: fullName,
      image: currUser.avatar_url ?? currUser.avatar,
      // user_zoom_status is keyed against the original interview
      // creator in edit mode; only apply it when its id matches the
      // current user's id, otherwise leave undefined (the chip skips
      // the tag rather than rendering a misleading status).
      zoomStatus:
        userZoomStatus &&
        (userZoomStatus.id === undefined ||
          String(userZoomStatus.id) === String(currUser.id))
          ? {
              can_host_zoom_meeting: userZoomStatus.can_host_zoom_meeting,
              zoom_rate_limit_reached: userZoomStatus.zoom_rate_limit_reached,
              zoom_plan: userZoomStatus.zoom_plan,
              zoom_plan_type: userZoomStatus.zoom_plan_type,
            }
          : undefined,
      meetEligible:
        userZoomStatus &&
        (userZoomStatus.id === undefined ||
          String(userZoomStatus.id) === String(currUser.id))
          ? userZoomStatus.can_host_gmeet_meeting
          : undefined,
    };
  }, [
    apiReference,
    interviewPlan?.user_zoom_status,
    currUser?.id,
    currUser?.first_name,
    currUser?.last_name,
    currUser?.avatar_url,
    currUser?.avatar,
    userZoomStatus,
  ]);

  // The REAL logged-in user id — distinct from `currentUserHostInfo`,
  // which becomes the interview creator in edit mode. Consumers that mean
  // "you" (e.g. hiding the set-timezone globe on your own interviewer
  // chip) must use this, not the host-candidate id.
  const loggedInUserId = currUser?.id;

  // Build the actual participant options for the Host field — only
  // currently-selected interviewers, NOT the full plan pool.
  // `sharedState.interviewerIds` is the source of truth; we resolve
  // each id against (a) `participant_default_options` (the chip
  // hydration source on edit / pool flows) and (b) the
  // `interviewerOptionCache` (populated as the user searches the
  // async-select). This matches legacy `SchZoomHost`'s reliance on
  // `interviewersDetails` (= picked interviewers) instead of the
  // wider plan pool.
  const selectedHostParticipantOptions = useMemo(() => {
    const defaults = (payloadRef.current.details?.participant_default_options ??
      []) as Array<{
      value: string;
      label: string;
      image?: string;
      zoomStatus?: HostZoomStatus;
      meetEligible?: boolean;
    }>;
    const defaultsById = new Map(
      defaults.map((d) => [String(d.value), d] as const)
    );
    // SCH-43: `sharedState.interviewerIds` (Zustand) is seeded by a
    // post-mount effect in `DetailsStep`, so on first render it's empty
    // even when the plan synchronously seeded `participant_ids` in
    // `payloadRef.current.details`. Reading from Zustand alone meant
    // the auto-pick effect saw an empty roster and silently locked
    // `host_id` to the recruiter, and the Zoom location-fallback flipped
    // `location` off Zoom (→ google_meet) on cache-warm reopens — both
    // never recovered once the store caught up. Backfill from the
    // form-seed while Zustand is lagging; once `liveIds` populates we
    // read it as before.
    const liveIds = sharedState.interviewerIds.map(String);
    let activeIds: string[];
    if (liveIds.length > 0) {
      activeIds = liveIds;
    } else {
      const seedRaw = (payloadRef.current.details?.participant_ids ?? []) as
        | unknown[]
        | string
        | number;
      const seedArr = Array.isArray(seedRaw)
        ? seedRaw
        : seedRaw
          ? [seedRaw]
          : [];
      activeIds = seedArr
        .map((id) =>
          typeof id === 'string' || typeof id === 'number' ? String(id) : null
        )
        .filter((s): s is string => Boolean(s));
    }
    return activeIds
      .map((id) => {
        const key = String(id);
        const fromDefaults = defaultsById.get(key);
        if (fromDefaults) return fromDefaults;
        const fromCache = getCachedInterviewerOption(key);
        if (fromCache) {
          return {
            value: key,
            label: fromCache.label,
            image: fromCache.image,
            zoomStatus: fromCache.zoomStatus,
            meetEligible: fromCache.meetEligible,
          };
        }
        return null;
      })
      .filter(
        (
          o
        ): o is {
          value: string;
          label: string;
          image?: string;
          zoomStatus?: HostZoomStatus;
          meetEligible?: boolean;
        } => o !== null
      );
  }, [sharedState.interviewerIds, payloadRef]);

  // In edit mode, the existing interview's `host` block carries the
  // saved host with their current zoom status. Surface as a Select
  // option so the chip still renders even when the interviewer list
  // has been swapped since the original schedule.
  const editSavedHost = useMemo(() => {
    if (apiReference !== 'edit') return undefined;
    const host = interviewPlan?.host as
      | (HostZoomStatus & {
          id?: number | string;
          name?: string;
          avatar?: string;
          avatar_url?: string;
        })
      | null
      | undefined;
    if (!host?.id) return undefined;
    return {
      value: String(host.id),
      label: host.name ?? `User ${host.id}`,
      image: host.avatar_url ?? host.avatar,
      zoomStatus: {
        can_host_zoom_meeting: host.can_host_zoom_meeting,
        zoom_rate_limit_reached: host.zoom_rate_limit_reached,
        zoom_plan: host.zoom_plan,
        zoom_plan_type: host.zoom_plan_type,
      },
    };
  }, [apiReference, interviewPlan?.host]);

  // ── Host auto-selection (CREATE only) ───────────────────────────────
  // EDIT short-circuits at the top: a saved host is preserved
  // verbatim (legacy `SchZoomHost` parity — CORE-4039). CREATE
  // strategy below:
  //   1. If `host_id` is already set (BE pre-fill on plan) AND the
  //      user behind it is still a valid Zoom host, keep it as-is.
  //   2. If `host_id` is set but the user is EXPLICITLY invalid (BE
  //      returned `can_host_zoom_meeting: false`, rate-limited, or
  //      unlicensed), clear it and re-pick from `pickAutoHostId`. This
  //      handles the "user lost Zoom access since plan was created"
  //      edge case the user flagged.
  //   3. If `host_id` is empty, run `pickAutoHostId` and use its result
  //      (which respects the plan's `zoom_host_type` to decide order).
  //
  // Reactivity: depends on `selectedHostParticipantOptions` so the
  // effect re-runs whenever the picked-interviewer list (with their
  // zoom_status) changes — e.g. roster additions / removals — rather
  // than only when the id-array changes. Stable refs across renders
  // keep this from looping.
  // Non-adhoc reads zoom_host_type off the raw plan response;
  // adhoc keeps `interviewPlan` undefined for downstream branching,
  // so the same slice arrives as a separate prop (legacy parity:
  // legacy adhoc always had this field on its plan response and
  // pickAutoHostId honored it).
  const planHostType =
    (interviewPlan?.zoom_host_type as
      | 'organizer'
      | 'interviewer'
      | string
      | null
      | undefined) ?? planZoomHostTypeProp;

  // Interview kind for the SCH-37 lock check. Read from payloadRef
  // (synchronously seeded by `hydratePlanDefaults` / `fromEditApi`
  // before this hook runs) first, then from the live form once
  // FormBuilder has mounted, with `sharedState.interviewKind` as the
  // re-render trigger for user-driven changes.
  //
  // `sharedState.interviewKind` ALONE is unsafe here because Zustand
  // defaults to `'one_on_one'` and only catches up to `'panel'` after
  // DetailsStep's post-mount seed effect runs. For a plan-loaded
  // panel + self-schedule with ≥2 interviewers, the first render
  // would briefly look like a multi-interviewer 1:1 → `hostLocked`
  // true → enforcer #2 below writes the resolved organizer to
  // `host_id` before the seed lands → the auto-pick effect then
  // preserves that valid (organizer) host, contradicting the plan's
  // `zoom_host_type: 'interviewer'` intent. Reading the authoritative
  // payload/form value keeps `hostLocked` correct from the very first
  // render.
  const effectiveInterviewKind: 'one_on_one' | 'panel' = (() => {
    const live = (
      formRef.current?.getValues() as Record<string, unknown> | undefined
    )?.interview_type;
    if (live === 'panel' || live === 'one_on_one') return live;
    const seeded = (
      payloadRef.current.details as Record<string, unknown> | undefined
    )?.interview_type;
    if (seeded === 'panel' || seeded === 'one_on_one') return seeded;
    return sharedState.interviewKind;
  })();

  // SCH-37: self-schedule one_on_one with MULTIPLE interviewers → Host
  // locks. ONLY this mode; every other mode (and single-interviewer
  // one_on_one self-schedule, where the attendee IS known) keeps the
  // existing all-candidate `pickAutoHostId` logic untouched. Same
  // condition `useDetailsFormSchema` uses to disable the field. Here
  // we need it to (a) skip the auto-host effect, (b) drive the
  // Zoom-eligibility gate, (c) enforce the value on mount / async-load
  // (enforcer #2).
  const hostLocked =
    isSelfSchedule &&
    effectiveInterviewKind === 'one_on_one' &&
    selectedHostParticipantOptions.length > 1;

  // Resolved locked host: organizer → first valid interviewer → ''
  // (nobody can host → Zoom disabled / falls back). Same
  // `resolveLockedHostId` the schema uses, so the Zoom gate, the
  // schema lock effect and this enforcer can never disagree.
  const lockedHostId = hostLocked
    ? resolveLockedHostId({
        organizer: currentUserHostInfo
          ? {
              id: currentUserHostInfo.id,
              zoomStatus: currentUserHostInfo.zoomStatus,
            }
          : undefined,
        participantOptions: selectedHostParticipantOptions,
      })
    : '';

  // Enforcer #2 — mount / data-ready. FormBuilder's schema FieldEffect
  // (enforcer #1) deliberately SKIPS its first run and only reacts to
  // form-field changes, so it can't cover: initial edit-mount, create
  // from a plan with ≥2 interviewers preselected, or zoom-status
  // arriving async after mount (none of which change a watched field).
  // This effect keys on the reactive React inputs that determine the
  // locked host and reads the REAL form location imperatively (never
  // the broken `sharedState.location` mirror). Idempotent — writes
  // only when different — so it can't loop and can't fight #1.
  useEffect(() => {
    if (!hostLocked || !lockedHostId) return;
    const formApi = formRef.current;
    if (!formApi) return;
    const values = formApi.getValues() as Record<string, unknown>;
    if (values.location !== 'zoom') return;
    if (((values.host_id ?? '') as string) === lockedHostId) return;
    formApi.setValue('host_id', lockedHostId);
  }, [
    hostLocked,
    lockedHostId,
    currentUserHostInfo,
    selectedHostParticipantOptions,
    formRef,
  ]);

  // SCH-50: tracks the value this effect last auto-picked. Lets the
  // preserve check distinguish auto-fills (re-evaluate when zoom_status
  // catches up) from user / BE / saved-host picks (always preserve).
  // Without it, an auto-pick that fell back to the organiser while
  // interviewer statuses were still loading was locked in forever once
  // those statuses arrived — even when the plan asks for an interviewer
  // host and one became eligible. Stays null until the effect first
  // writes; only updated alongside `setValue`, so user picks via
  // FormBuilder's onChange never match it.
  const lastAutoPickedHostRef = useRef<string | null>(null);

  useEffect(() => {
    const formApi = formRef.current;
    if (!formApi) return;
    const values = formApi.getValues() as Record<string, unknown>;
    if (values.location !== 'zoom') return;

    // When locked, the schema lock effect owns `host_id` (forces the
    // resolved host). Bail so `pickAutoHostId` can't propose an
    // interviewer or clear the value.
    if (hostLocked) return;

    // Build the unified candidate list (interviewers + edit-saved
    // host + current user) so we can validate any existing
    // `host_id` against it.
    const candidates: Array<{
      value: string;
      zoomStatus?: HostZoomStatus;
    }> = [...selectedHostParticipantOptions];
    if (editSavedHost) {
      candidates.push({
        value: editSavedHost.value,
        zoomStatus: editSavedHost.zoomStatus,
      });
    }
    if (currentUserHostInfo) {
      candidates.push({
        value: String(currentUserHostInfo.id),
        zoomStatus: currentUserHostInfo.zoomStatus,
      });
    }

    const currentHostId = (values.host_id ?? '') as string;

    // EDIT with an existing host: legacy `SchZoomHost` parity — the
    // create-time `pickAutoHostId` autofill NEVER clobbers a saved
    // host on edit. Preserve it while it's still a LEGAL Zoom host —
    // one of the currently-selected interviewers or the organizer /
    // current-user candidate — or while the roster is still hydrating
    // (no candidates resolved yet). If the user removed that person
    // during THIS edit, clear it so the required-field error forces a
    // manual pick — NEVER silently substitute. Re-picking would rewrite
    // `host_id`, flip the BE's `will_save_change_to_host_id?` true, and
    // 422 `err_host_id_invalid` when the editor isn't the interview's
    // creator or an interviewer (CORE-4039). Edit with NO host
    // (e.g. Location switched to Zoom mid-edit) falls through to the
    // shared auto-pick below — nothing to preserve, and legacy
    // auto-selects there too.
    //
    // SCH-71: `editSavedHost` (the originally-saved host) is
    // deliberately NOT a validity source here. It stays a render-only
    // option so the chip still shows, but Zoom requires the host to be
    // the current user or a CURRENT interviewer — the moment the saved
    // host is removed from the roster (and isn't the current user) they
    // are no longer a legal host and the BE 404s every slot's meeting
    // (e.g. host = removed interviewer while the roster is the other
    // two). Trusting `editSavedHost`/`isSavedHost` preserved that stale
    // host forever — the bug. Mirrors the already-correct locked path
    // (`resolveLockedHostId`), which only ever considers organizer +
    // current interviewers.
    if (apiReference === 'edit' && currentHostId) {
      const validHostIds = [
        ...selectedHostParticipantOptions.map((o) => o.value),
        ...(currentUserHostInfo ? [String(currentUserHostInfo.id)] : []),
      ];
      const stillValidHost = validHostIds.includes(currentHostId);
      if (stillValidHost || validHostIds.length === 0) return;
      formApi.setValue('host_id', '');
      void formApi.trigger('host_id');
      return;
    }

    // CREATE (or EDIT with no host to preserve) below.
    if (currentHostId) {
      const existing = candidates.find((c) => c.value === currentHostId);
      if (existing) {
        // In the roster — keep unless EXPLICITLY invalid. Unknown
        // zoom_status means "not loaded yet"; stripping then would
        // wipe edit-hydrated values during initial load.
        if (!isExplicitlyInvalidHost(existing.zoomStatus)) {
          // SCH-50: re-evaluate when the value we previously
          // auto-picked is still in place. On plan-loaded panel
          // self-schedule the first auto-pick runs before
          // interviewer zoom_status loads, so `pickAutoHostId`
          // initially falls back to the organiser; preserving that
          // forever skips the plan's `zoom_host_type: 'interviewer'`
          // intent once a valid interviewer becomes available. Form
          // values changed by the user (FormBuilder onChange) won't
          // match the ref, so a user-pick is still preserved here.
          if (currentHostId !== lastAutoPickedHostRef.current) {
            return;
          }
          const reEvaluated = pickAutoHostId({
            participantOptions: selectedHostParticipantOptions,
            currentUser: currentUserHostInfo
              ? {
                  id: currentUserHostInfo.id,
                  zoomStatus: currentUserHostInfo.zoomStatus,
                }
              : undefined,
            planHostType,
          });
          if (reEvaluated === currentHostId) return;
          // else: best pick has shifted (zoom_status loaded) — fall
          // through and overwrite our previous auto-pick.
        }
      } else if (candidates.length === 0) {
        // Candidate list hasn't populated yet (edit-mode hydration in
        // flight). Keep the hydrated host_id rather than stripping it.
        return;
      }
      // else: host_id points at someone no longer in the candidate
      // roster (interviewer was swapped). Fall through to auto-pick,
      // which clears or replaces based on the new roster.
    }

    const picked = pickAutoHostId({
      participantOptions: selectedHostParticipantOptions,
      currentUser: currentUserHostInfo
        ? {
            id: currentUserHostInfo.id,
            zoomStatus: currentUserHostInfo.zoomStatus,
          }
        : undefined,
      planHostType,
    });
    const next = picked ?? '';
    if (next !== currentHostId) {
      lastAutoPickedHostRef.current = picked;
      formApi.setValue('host_id', next);
    }
  }, [
    apiReference,
    editSavedHost,
    selectedHostParticipantOptions,
    currentUserHostInfo,
    planHostType,
    hostLocked,
    // Re-run when the live location changes (e.g. google_meet → zoom). The effect
    // reads the reliable `values.location` imperatively, but without a reactive
    // location trigger it never re-ran on a provider swap — so a host picked for
    // Google Meet (invalid for Zoom) was left in place (CORE-5136). `sharedState.
    // location` mirrors the form change (same trigger the note-taker heal uses),
    // and the effect is idempotent, so an extra run is harmless.
    sharedState.location,
    formRef,
  ]);

  // ── Zoom location auto-fallback ─────────────────────────────────────
  // When every candidate host (picked interviewers + recruiter + edit-
  // saved host) is "Not part of Zoom" / rate-limited, the Zoom option
  // is disabled in the Location dropdown. If the form already has
  // `location: 'zoom'` (default value or prior valid state), switch it
  // to the next enabled option so the recruiter isn't stranded on an
  // empty Host dropdown.
  const zoomHasNoEligibleHost = useMemo(() => {
    // SCH-37 locked mode: the host is resolved (organizer → else a
    // valid interviewer). Zoom is unusable ONLY when that resolution
    // yields nobody (`''`). An eligible interviewer DOES keep Zoom
    // alive — it just becomes the forced host. Same `resolveLockedHostId`
    // the lock effect + field defaultValue use, so the gate and the
    // forced host can never disagree.
    if (hostLocked) return lockedHostId === '';
    const candidates = [
      ...selectedHostParticipantOptions,
      ...(currentUserHostInfo ? [currentUserHostInfo] : []),
      ...(editSavedHost ? [editSavedHost] : []),
    ];
    if (candidates.length === 0) return false;
    return candidates.every((c) => isHostOptionDisabled(c.zoomStatus));
  }, [
    hostLocked,
    lockedHostId,
    selectedHostParticipantOptions,
    currentUserHostInfo,
    editSavedHost,
  ]);
  const { defaultLocation: fallbackLocation } = useLocationFieldConfig({
    zoomHasNoEligibleHost,
  });
  useEffect(() => {
    if (!zoomHasNoEligibleHost) return;
    // EDIT: the saved `location` is authoritative — legacy `SchLocation`
    // never auto-switched an existing interview's location. Silently
    // rewriting it here flipped a saved Zoom interview to the next
    // enabled option (e.g. Teams, since google_meet is filtered out)
    // whenever host eligibility looked empty (incl. transiently while
    // it loaded). A host problem on edit is surfaced via the disabled
    // Host option / required validation, NOT by mutating the location.
    if (apiReference === 'edit') return;
    const formApi = formRef.current;
    if (!formApi) return;
    const values = formApi.getValues() as Record<string, unknown>;
    if (values.location !== 'zoom') return;
    if (!fallbackLocation || fallbackLocation === 'zoom') return;
    formApi.setValue('location', fallbackLocation);
    // Host is conditional on `location==='zoom'`. FormBuilder wraps
    // the hidden field as `union([z.undefined(), z.string().min(1)])`
    // — an empty-string `host_id: ''` is NEITHER undefined NOR
    // length≥1, so it fails the union with `too_small` ("Host is
    // required") even while hidden. `trigger` alone can't fix it
    // (re-validating '' always fails). Reset the value to `undefined`
    // so the `z.undefined()` branch passes, THEN re-validate just
    // this field (NOT the whole form — that would surface every
    // required-field error on mount before the user has touched
    // anything).
    formApi.setValue('host_id', undefined);
    void formApi.trigger('host_id');
  }, [zoomHasNoEligibleHost, fallbackLocation, formRef, apiReference]);

  return {
    currentUserHostInfo,
    loggedInUserId,
    selectedHostParticipantOptions,
    editSavedHost,
  };
}
